import { Palette } from '@mui/material/styles';

const getBorderColor = (
  { selected, warning, error }: { [key: string]: boolean | string },
  palette: Palette,
) => {
  if (error) {
    return palette.error.main;
  }
  if (warning) {
    return palette.warning.main;
  }
  if (selected) {
    return palette.tertiary.main;
  }
};
const getBoxShadow = ({
  readOnly,
  disabled,
  hoverShadow,
}: {
  [key: string]: boolean | string;
}) => {
  if (readOnly) {
    return '0px 1px 2px 0px rgba(0, 0, 0, 0.15), 0px 0px 5px 0px rgba(23, 24, 24, 0.05)';
  }
  if (disabled) {
    return 'none';
  }
  switch (hoverShadow) {
    case 'hoverSmall':
      return '-7px -1px 10px -4px rgba(16, 24, 40, 0.1),2px 6px 4px -3px rgba(20, 24, 40, 0.1)';
    case 'hoverMedium':
      return '1px 8px 16px -1px rgba(16, 24, 40, 0.20)';
    case 'hoverBig':
      return '1px 8px 10px 8px rgba(16, 24, 40, 0.06), 0px 4px 8px -2px rgba(16, 24, 40, 0.1)';
    case 'default':
      return '0px 2px 4px -2px rgba(16, 24, 40, 0.06), 0px 4px 8px -2px rgba(16, 24, 40, 0.1)';
    case 'noHover':
      return '0px 1px 2px 0px rgba(0, 0, 0, 0.15), 0px 0px 5px 0px rgba(23, 24, 24, 0.05)';
    default:
      return '0px 2px 4px -2px rgba(16, 24, 40, 0.06), 0px 4px 8px -2px rgba(16, 24, 40, 0.1)';
  }
};

const CardOverrides = (palette: Palette) => {
  return {
    styleOverrides: {
      root: (ownerState: { [key: string]: boolean | string }) => ({
        pointerEvents:
          ownerState.disabled || ownerState.readOnly ? 'none' : 'auto',
        boxShadow: ownerState.disabled
          ? 'none'
          : '0px 1px 2px 0px rgba(0, 0, 0, 0.15), 0px 0px 5px 0px rgba(23, 24, 24, 0.05)',
        '&:hover': {
          boxShadow: getBoxShadow(ownerState),
          backgroundColor:
            !ownerState.readOnly &&
            !ownerState.disabled &&
            !ownerState.selected &&
            ownerState.noHover
              ? palette.ui.secondary
              : undefined,
        },
        '&.MuiPaper-elevation': {
          border: `1px solid ${palette.border.tertiary}`,
        },
        '&.MuiPaper-outlined': {
          boxShadow: 'none',
          border: `1px solid ${palette.border.secondary}`,
          '&:hover': { border: `1px solid ${palette.border.secondary}` },
          'Mui-disabled': { border: `1px solid ${palette.border.disabled}` },
        },
        '&.MuiPaper-elevation, &.MuiPaper-outlined': {
          border: `1px solid ${getBorderColor(
            {
              ...ownerState,
            },
            palette,
          )}`,
        },
        '&.MuiPaper-root': {
          borderRadius: '8px',
        },
        backgroundColor: ownerState.selected
          ? palette.background.tertiary
          : palette.ui.primary,
        ...(ownerState.ai && {
          overflow: 'inherit',
          position: 'relative',
          backgroundClip: 'padding-box',
          border: 'solid 1px transparent !important',
          '@keyframes gradientShift': {
            '0%': {
              backgroundPosition: '0% 50%',
              opacity: 0.7,
            },
            '35%': {
              backgroundPosition: '100% 50%',
              opacity: 1,
            },
            '50%': {
              backgroundPosition: '100% 100%',
              opacity: 0.8,
            },
            '75%': {
              backgroundPosition: '0% 100%',
              opacity: 1,
            },
            '100%': {
              backgroundPosition: '0% 50%',
              opacity: 0.4,
            },
          },
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: -1,
            margin: '-1px',
            borderRadius: 'inherit',
            background: palette.ai.gradient,
          },
          '&:hover:before': {
            content: '""',
            animation: 'gradientShift 2s ease-in-out infinite',
          },
          '&:hover': {
            boxShadow: `
            6px -6px 6px 2px rgba(130, 245, 117, 0.13),
            -6px 6px 6px 2px rgba(122, 237, 231, 0.13) !important
          `,
          },
        }),
      }),
    },
  };
};

export default CardOverrides;
